import { CircleAlert } from 'lucide-react'
import { type ReactNode } from 'react'
import { describeError } from '@/lib/api-error'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

type ErrorStateProps = {
  error: unknown
  className?: string
  onRetry?: () => void
  retryLabel?: string
  children?: ReactNode
}

export function ErrorState({
  error,
  className,
  onRetry,
  retryLabel = 'Try again',
  children,
}: ErrorStateProps) {
  const desc = describeError(error)
  return (
    <Alert
      variant="destructive"
      className={cn('flex flex-col gap-3 [&>svg]:text-destructive', className)}
    >
      <CircleAlert />
      <AlertTitle>{desc.title}</AlertTitle>
      <AlertDescription className="space-y-2">
        <p className="text-sm">{desc.message}</p>
        {desc.code || desc.requestId ? (
          <p className="text-[11px] font-mono text-muted-foreground">
            {desc.code ? <span>code: {desc.code}</span> : null}
            {desc.code && desc.requestId ? ' · ' : null}
            {desc.requestId ? <span>request_id: {desc.requestId}</span> : null}
          </p>
        ) : null}
        {desc.details ? (
          <details className="text-[11px]">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Details
            </summary>
            <pre className="mt-2 overflow-auto rounded-md bg-muted/50 p-2 font-mono">
              {JSON.stringify(desc.details, null, 2)}
            </pre>
          </details>
        ) : null}
        {children}
        {onRetry ? (
          <div className="pt-1">
            <Button size="sm" variant="outline" onClick={onRetry}>
              {retryLabel}
            </Button>
          </div>
        ) : null}
      </AlertDescription>
    </Alert>
  )
}
